import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Select, Input, Space, Tag, Tooltip, Modal, Form, Tabs, message, Progress, Radio, DatePicker, Checkbox, Dropdown, Drawer, Popconfirm } from 'antd';
import { ReloadOutlined, KeyOutlined, PlayCircleOutlined, CopyOutlined, DownloadOutlined, UnorderedListOutlined, PauseCircleOutlined, StopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useMenuPermission } from '../hooks/useMenuPermission';
import { useIsMobile } from '../hooks/useIsMobile';
import RangeFilter, { RANGE_PRESETS, GROWTH_PRESETS } from '../components/RangeFilter';

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const STATUS_META = {
  up: { text: '增长', color: 'green' },
  flat: { text: '0增长', color: 'orange' },
  down: { text: '掉粉', color: 'red' },
  failed: { text: '查询失败', color: 'default' },
  '未查询': { text: '未查询', color: 'default' }
};

const JOB_STATUS_META = {
  running: { text: '运行中', color: 'processing' },
  paused: { text: '已暂停', color: 'warning' },
  done: { text: '已完成', color: 'success' },
  error: { text: '已中断', color: 'error' },
  cancelled: { text: '已终止', color: 'default' }
};

function GrowthValue({ value }) {
  if (value === null || value === undefined) return <span style={{ color: '#bfbfbf' }}>—</span>;
  const n = Number(value);
  const color = n > 0 ? '#389e0d' : n < 0 ? '#cf1322' : '#8c8c8c';
  const text = n > 0 ? `+${n}` : String(n);
  return <span style={{ color, fontWeight: n !== 0 ? 600 : 400 }}>{text}</span>;
}

function FanMonitor() {
  const { canEdit } = useMenuPermission();
  const isMobile = useIsMobile();
  const [user] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch (e) { return {}; } });
  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.username === 'admin';

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [dates, setDates] = useState([]);
  const [summary, setSummary] = useState({ total: 0, today_growth_sum: 0, zero_growth: 0, falling: 0, failed: 0 });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sortField, setSortField] = useState(undefined);
  const [sortOrder, setSortOrder] = useState(undefined);

  const [statuses, setStatuses] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [growthMethods, setGrowthMethods] = useState([]);
  const [searchMode, setSearchMode] = useState('simple');
  const [effectiveFansPreset, setEffectiveFansPreset] = useState('all');
  const [initialFansPreset, setInitialFansPreset] = useState('all');
  const [finalFansPreset, setFinalFansPreset] = useState('all');
  const [growthCountPreset, setGrowthCountPreset] = useState('all');

  const [filters, setFilters] = useState({
    status_ids: [],
    platform: '抖音',
    supplier_id: [],
    growth_method: [],
    target_status: 'all',
    team: [],
    handler: undefined,
    keyword: '',
    keywords: '',
    search_field: 'all',
    match_type: 'fuzzy',
    effective_fans_min: undefined,
    effective_fans_max: undefined,
    initial_fans_min: undefined,
    initial_fans_max: undefined,
    final_fans_min: undefined,
    final_fans_max: undefined,
    growth_count_min: undefined,
    growth_count_max: undefined,
    dateRange: null,
    completeDateRange: null,
    updatedDateRange: null,
    days: 7
  });

  const [fetching, setFetching] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const fetchStartRef = useRef(null);
  const [tick, setTick] = useState(Date.now());

  const [cookieVisible, setCookieVisible] = useState(false);
  const [cookies, setCookies] = useState([]);
  const [cookieTab, setCookieTab] = useState('global');
  const [cookieForm] = Form.useForm();

  // 任务管理
  const [taskVisible, setTaskVisible] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [taskTotal, setTaskTotal] = useState(0);
  const [taskPage, setTaskPage] = useState(1);
  const [taskPageSize, setTaskPageSize] = useState(10);
  const [taskLoading, setTaskLoading] = useState(false);
  const taskTimerRef = useRef(null);

  // 粉丝快照保留期（页面上提示「保留 N 天自动清理」，免得忘了这条规则）
  const [retention, setRetention] = useState({ days: 180, last_run: null });
  useEffect(() => {
    api.get('/fan-monitor/retention')
      .then(res => { if (res.code === 200 && res.data) setRetention(res.data); })
      .catch(() => {});
  }, []);

  // 基础选项
  useEffect(() => {
    Promise.all([
      api.get('/statuses').catch(() => ({ data: [] })),
      api.get('/platforms').catch(() => ({ data: [] })),
      isAdmin ? api.get('/suppliers/all').catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      api.get('/teams').catch(() => ({ data: [] })),
      api.get('/growth-methods').catch(() => ({ data: [] }))
    ]).then(([st, pf, sp, tm, gm]) => {
      const stList = st.data || [];
      setStatuses(stList);
      setPlatforms(pf.data || []);
      setSuppliers(sp.data || []);
      setTeams(tm.data || []);
      setGrowthMethods(gm.data || []);
      // 默认监控：涨粉中 + 已完成待确认
      const defaults = stList.filter(s => ['涨粉中', '已完成待确认'].includes(s.name)).map(s => s.id);
      setFilters(prev => ({ ...prev, status_ids: defaults }));
    });
  }, [isAdmin]);

  const buildParams = (f) => {
    const params = { days: f.days };
    // 「全部」显式传 __all__；不传时后端默认「涨粉中 + 已完成待确认」
    if (f.status_ids && f.status_ids.includes('__all__')) {
      params.status_ids = '__all__';
    } else if (f.status_ids && f.status_ids.length > 0) {
      params.status_ids = f.status_ids.join(',');
    }
    if (f.platform) params.platform = f.platform;
    if (f.supplier_id && f.supplier_id.length > 0) params.supplier_id = f.supplier_id.join(',');
    if (f.growth_method && f.growth_method.length > 0) params.growth_method = f.growth_method.join(',');
    if (f.target_status && f.target_status !== 'all') params.target_status = f.target_status;
    if (f.team && f.team.length > 0) params.team = f.team.join(',');
    if (f.handler) params.handler = f.handler;
    if (searchMode === 'batch' && f.keywords) {
      params.keywords = f.keywords;
      params.search_field = f.search_field;
      params.match_type = f.match_type;
    } else if (f.keyword) {
      params.keyword = f.keyword;
      params.search_field = f.search_field;
      params.match_type = f.match_type;
    }
    ['effective_fans', 'initial_fans', 'final_fans', 'growth_count'].forEach(k => {
      if (f[`${k}_min`] !== undefined && f[`${k}_min`] !== null) params[`${k}_min`] = f[`${k}_min`];
      if (f[`${k}_max`] !== undefined && f[`${k}_max`] !== null) params[`${k}_max`] = f[`${k}_max`];
    });
    if (f.dateRange && f.dateRange[0]) {
      params.order_start_date = f.dateRange[0].format('YYYY-MM-DD');
      params.order_end_date = f.dateRange[1].format('YYYY-MM-DD');
    }
    if (f.completeDateRange && f.completeDateRange[0]) {
      params.complete_date_start = f.completeDateRange[0].format('YYYY-MM-DD');
      params.complete_date_end = f.completeDateRange[1].format('YYYY-MM-DD');
    }
    if (f.updatedDateRange && f.updatedDateRange[0]) {
      params.updated_date_start = f.updatedDateRange[0].format('YYYY-MM-DD');
      params.updated_date_end = f.updatedDateRange[1].format('YYYY-MM-DD');
    }
    if (sortField && sortOrder) {
      params.sort_field = sortField;
      params.sort_order = sortOrder;
    }
    return params;
  };

  const loadData = useCallback(async (p = page, ps = pageSize, f = filters) => {
    setLoading(true);
    try {
      const params = { page: p, pageSize: ps, ...buildParams(f) };
      const res = await api.get('/fan-monitor/orders', { params });
      if (res.code === 200) {
        setData(res.data.list || []);
        setTotal(res.data.total || 0);
        setDates(res.data.dates || []);
        setSummary(res.data.summary || {});
      }
    } catch (e) {}
    setLoading(false);
  }, [page, pageSize, filters, sortField, sortOrder]);

  useEffect(() => { loadData(); }, [page, pageSize, filters, sortField, sortOrder]);

  // 复制选中的抖音号
  const handleCopyAccounts = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先勾选要复制的账号');
      return;
    }
    const text = data.filter(r => selectedRowKeys.includes(r.order_id)).map(r => r.account_name).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      message.success(`已复制 ${selectedRowKeys.length} 个抖音号`);
    } catch (e) {
      // 兼容旧浏览器
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      message.success(`已复制 ${selectedRowKeys.length} 个抖音号`);
    }
  };

  // 导出 CSV（筛选数据 / 选中数据）
  const handleExport = async (mode) => {
    try {
      let rows = data;
      if (mode === 'selected') {
        if (selectedRowKeys.length === 0) {
          message.warning('请先勾选要导出的账号');
          return;
        }
        rows = data.filter(r => selectedRowKeys.includes(r.order_id));
      } else {
        // 导出全部筛选结果（不只当前页）
        const params = { page: 1, pageSize: 5000, ...buildParams(filters) };
        const res = await api.get('/fan-monitor/orders', { params });
        if (res.code === 200) rows = res.data.list || [];
      }

      const headers = ['抖音号', '昵称', '主体', '员工', '小组', '当前粉丝', '上次', '本次增长', '今日增长', '累计增长', '状态', '初始粉丝', '目标', '剩余进度', ...dates];
      const lines = [headers.join(',')];
      rows.forEach(r => {
        const meta = STATUS_META[r.status] || STATUS_META['未查询'];
        const base = [
          r.account_name || '', r.nickname || '', r.supplier_name || '', r.handler || '', r.team || '',
          r.current_fans ?? '', r.prev_fans ?? '', r.delta ?? '', r.today_growth ?? '', r.cum_growth ?? '',
          meta.text, r.initial_fans ?? '', r.growth_target ?? '', r.remaining ?? ''
        ];
        const dayCells = dates.map(d => {
          const c = r.daily ? r.daily[d] : null;
          if (!c) return '';
          return c.growth === null || c.growth === undefined ? String(c.fans) : `${c.fans} (${c.growth > 0 ? '+' : ''}${c.growth})`;
        });
        lines.push([...base, ...dayCells].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
      });

      const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `涨粉监控_${dayjs().format('YYYYMMDD_HHmm')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success(`已导出 ${rows.length} 条`);
    } catch (e) {
      message.error('导出失败');
    }
  };

  // 拉取
  const startFetch = async (mode = 'filtered') => {
    if (mode === 'selected' && selectedRowKeys.length === 0) {
      message.warning('请先勾选要拉取的账号');
      return;
    }
    const count = mode === 'selected' ? selectedRowKeys.length : (summary.total || 0);
    if (mode === 'filtered' && filters.status_ids.length === 0 && !window.confirm('未选择状态，将按默认状态（涨粉中 + 已完成待确认）拉取，确定？')) return;

    if (count > 300) {
      const minutes = Math.ceil((count * 1.5) / 60);
      const ok = window.confirm(`本次将拉取 ${count} 个抖音号，串行查询预计约 ${minutes} 分钟，是否继续？`);
      if (!ok) return;
    }

    try {
      const body = mode === 'selected'
        ? { order_ids: selectedRowKeys }
        : buildParams(filters);
      const res = await api.post('/fan-monitor/fetch', body);
      if (res.code === 200) {
        fetchStartRef.current = Date.now();
        setTick(Date.now());
        setFetching(true);
        setJob({ id: res.data.job_id, total: res.data.total, done: 0, success: 0, failed: 0, status: 'running' });
        pollJob(res.data.job_id);
      }
    } catch (e) {}
  };

  const pollJob = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/fan-monitor/jobs/${id}`);
        if (res.code === 200) {
          setJob(res.data);
          if (res.data.status !== 'running') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setFetching(false);
            if (res.data.status === 'error') {
              message.error(res.data.message || '拉取失败');
            } else if (res.data.status === 'paused') {
              message.info('任务已暂停，可在「任务管理」中继续');
            } else if (res.data.status === 'cancelled') {
              message.warning('任务已终止');
            } else {
              message.success(`拉取完成：成功 ${res.data.success}，失败 ${res.data.failed}`);
            }
            loadData();
          }
        }
      } catch (e) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
        setFetching(false);
        message.error('任务不存在或已过期，请重新拉取');
      }
    }, 1500);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // 拉取中每秒刷新一次，用于计算耗时/剩余时间
  useEffect(() => {
    if (!fetching) return;
    const t = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [fetching]);

  const formatDuration = (sec) => {
    if (!isFinite(sec) || sec < 0) return '--';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return m > 0 ? `${m}分${String(s).padStart(2, '0')}秒` : `${s}秒`;
  };

  // 任务管理
  const fetchTasks = useCallback(async (p = taskPage, ps = taskPageSize, silent = false) => {
    if (!silent) setTaskLoading(true);
    try {
      const res = await api.get('/fan-monitor/jobs', { params: { page: p, pageSize: ps } });
      if (res.code === 200) {
        setTasks(res.data.list || []);
        setTaskTotal(res.data.total || 0);
      }
    } catch (e) {}
    if (!silent) setTaskLoading(false);
  }, [taskPage, taskPageSize]);

  const openTasks = () => {
    setTaskVisible(true);
    setTaskPage(1);
    fetchTasks(1, taskPageSize);
  };

  const taskAction = async (id, action) => {
    try {
      const res = await api.post(`/fan-monitor/jobs/${id}/${action}`);
      if (res.code === 200) {
        message.success(res.message || '操作成功');
        fetchTasks();
      }
    } catch (e) {}
  };

  useEffect(() => {
    if (!taskVisible) {
      if (taskTimerRef.current) { clearInterval(taskTimerRef.current); taskTimerRef.current = null; }
      return;
    }
    fetchTasks(taskPage, taskPageSize, true);
    taskTimerRef.current = setInterval(() => fetchTasks(taskPage, taskPageSize, true), 3000);
    return () => {
      if (taskTimerRef.current) { clearInterval(taskTimerRef.current); taskTimerRef.current = null; }
    };
  }, [taskVisible, taskPage, taskPageSize]);

  const renderTaskActions = (r) => {
    const btns = [];
    if (r.status === 'running') {
      btns.push(
        <Button key="pause" size="small" icon={<PauseCircleOutlined />} disabled={!canEdit}
          onClick={() => taskAction(r.id, 'pause')}>暂停</Button>
      );
    }
    if ((r.status === 'paused' || r.status === 'error') && r.can_resume) {
      btns.push(
        <Button key="resume" size="small" type="primary" icon={<PlayCircleOutlined />} disabled={!canEdit}
          onClick={() => taskAction(r.id, 'resume')}>继续</Button>
      );
    }
    if (['running', 'paused', 'error'].includes(r.status)) {
      btns.push(
        <Popconfirm key="cancel" title="确定终止该任务？终止后不能继续" onConfirm={() => taskAction(r.id, 'cancel')}>
          <Button size="small" danger icon={<StopOutlined />} disabled={!canEdit}>终止</Button>
        </Popconfirm>
      );
    }
    return btns.length > 0 ? <Space size={4}>{btns}</Space> : <span style={{ color: '#bfbfbf' }}>—</span>;
  };

  // Cookie 管理
  const loadCookies = async () => {
    try {
      const res = await api.get('/fan-monitor/cookies');
      if (res.code === 200) setCookies(res.data || []);
    } catch (e) {}
  };

  const openCookies = () => {
    setCookieVisible(true);
    setCookieTab('global');
    cookieForm.resetFields();
    loadCookies();
  };

  const saveCookie = async () => {
    try {
      const values = await cookieForm.validateFields();
      const res = await api.post('/fan-monitor/cookies', values);
      if (res.code === 200) {
        message.success(res.message);
        cookieForm.resetFields();
        loadCookies();
      }
    } catch (e) {}
  };

  const toggleCookie = async (rec) => {
    try {
      const res = await api.put(`/fan-monitor/cookies/${rec.id}`, { enabled: rec.enabled ? 0 : 1 });
      if (res.code === 200) { message.success(res.message); loadCookies(); }
    } catch (e) {}
  };

  const deleteCookie = async (rec) => {
    if (!window.confirm('确定删除该 Cookie？')) return;
    try {
      const res = await api.delete(`/fan-monitor/cookies/${rec.id}`);
      if (res.code === 200) { message.success('已删除'); loadCookies(); }
    } catch (e) {}
  };

  // 表格列
  const baseColumns = [
    { title: '抖音号', dataIndex: 'account_name', width: 130, fixed: 'left', sorter: true },
    { title: '昵称', dataIndex: 'nickname', width: 120, ellipsis: true, sorter: true, render: v => v || '-' },
    { title: '主体', dataIndex: 'supplier_name', width: 100, sorter: true, render: v => v || '-' },
    { title: '员工', dataIndex: 'handler', width: 100, sorter: true, render: v => v || '-' },
    { title: '小组', dataIndex: 'team', width: 100, sorter: true, render: v => v || '-' },
    { title: '当前粉丝', dataIndex: 'current_fans', width: 100, align: 'right', sorter: true, render: v => v === null || v === undefined ? '—' : Number(v).toLocaleString() },
    { title: '上次', dataIndex: 'prev_fans', width: 90, align: 'right', sorter: true, render: v => v === null || v === undefined ? '—' : Number(v).toLocaleString() },
    { title: '本次增长', dataIndex: 'delta', width: 100, align: 'right', sorter: true, render: v => <GrowthValue value={v} /> },
    { title: '今日增长', dataIndex: 'today_growth', width: 100, align: 'right', sorter: true, render: v => <GrowthValue value={v} /> },
    { title: '累计增长', dataIndex: 'cum_growth', width: 100, align: 'right', sorter: true, render: v => <GrowthValue value={v} /> },
    {
      title: '状态', dataIndex: 'status', width: 90, sorter: true,
      render: (v, r) => {
        const meta = STATUS_META[v] || STATUS_META['未查询'];
        return <Tooltip title={r.last_error || ''}><Tag color={meta.color}>{meta.text}</Tag></Tooltip>;
      }
    },
    { title: '初始粉丝', dataIndex: 'initial_fans', width: 100, align: 'right', sorter: true, render: v => v === null || v === undefined ? '—' : Number(v).toLocaleString() },
    {
      title: '目标', dataIndex: 'growth_target', width: 90, align: 'right', sorter: true,
      render: v => (v === null || v === undefined) ? '—' : Number(v).toLocaleString()
    },
    {
      title: '剩余进度', dataIndex: 'remaining', width: 110, align: 'right', sorter: true,
      render: v => {
        if (v === null || v === undefined) return '—';
        if (v <= 0) return <span style={{ color: '#389e0d', fontWeight: 600 }}>{v}（已达标）</span>;
        return <span style={{ color: '#d46b08' }}>{v}</span>;
      }
    }
  ];

  const dateColumns = dates.map(d => ({
    title: d.slice(5),
    key: `daily:${d}`,
    width: 120,
    align: 'right',
    sorter: true,
    render: (_, r) => {
      const cell = r.daily ? r.daily[d] : null;
      if (!cell) return <span style={{ color: '#d9d9d9' }}>—</span>;
      return (
        <Tooltip title={cell.compare_date ? `对比 ${cell.compare_date}` : ''}>
          <span>
            {Number(cell.fans).toLocaleString()}{' '}
            {cell.growth !== null && cell.growth !== undefined && (
              <span style={{ color: cell.growth > 0 ? '#389e0d' : cell.growth < 0 ? '#cf1322' : '#8c8c8c', fontSize: 12 }}>
                {cell.growth > 0 ? `+${cell.growth}` : cell.growth}
              </span>
            )}
          </span>
        </Tooltip>
      );
    }
  }));

  const columns = isMobile ? [] : [...baseColumns, ...dateColumns];

  const mobileCards = (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Checkbox
          checked={data.length > 0 && data.every(r => selectedRowKeys.includes(r.order_id))}
          indeterminate={data.some(r => selectedRowKeys.includes(r.order_id)) && !data.every(r => selectedRowKeys.includes(r.order_id))}
          onChange={e => {
            const ids = data.map(r => r.order_id);
            setSelectedRowKeys(e.target.checked
              ? Array.from(new Set([...selectedRowKeys, ...ids]))
              : selectedRowKeys.filter(id => !ids.includes(id)));
          }}
        >全选本页</Checkbox>
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>已选 {selectedRowKeys.length}</span>
      </div>
      {data.map(r => {
        const meta = STATUS_META[r.status] || STATUS_META['未查询'];
        const recent = dates.slice(0, 7);
        return (
          <div key={r.order_id} style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <Checkbox
                checked={selectedRowKeys.includes(r.order_id)}
                onChange={e => {
                  setSelectedRowKeys(e.target.checked
                    ? [...selectedRowKeys, r.order_id]
                    : selectedRowKeys.filter(id => id !== r.order_id));
                }}
              >
                <span style={{ fontWeight: 600 }}>{r.account_name}</span>
              </Checkbox>
              <Tag color={meta.color}>{meta.text}</Tag>
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {[r.nickname, r.supplier_name, r.handler, r.team].filter(Boolean).join(' · ') || '—'}
            </div>
            <div style={{ background: '#fafafa', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>当前粉丝</div>
                  <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.2 }}>
                    {r.current_fans === null ? '—' : Number(r.current_fans).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>今日增长</div>
                  <div style={{ fontSize: 16, lineHeight: 1.2 }}><GrowthValue value={r.today_growth} /></div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>累计增长</div>
                  <div style={{ fontSize: 16, lineHeight: 1.2 }}><GrowthValue value={r.cum_growth} /></div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>初始粉丝</div>
                  <div style={{ fontSize: 14, lineHeight: 1.2 }}>
                    {r.initial_fans === null ? '—' : Number(r.initial_fans).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>目标</div>
                  <div style={{ fontSize: 14, lineHeight: 1.2 }}>
                    {r.growth_target === null || r.growth_target === undefined ? '—' : Number(r.growth_target).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 2 }}>剩余进度</div>
                  <div style={{ fontSize: 14, lineHeight: 1.2 }}>
                    {r.remaining === null || r.remaining === undefined
                      ? '—'
                      : (r.remaining <= 0
                        ? <span style={{ color: '#389e0d', fontWeight: 600 }}>{r.remaining}（已达标）</span>
                        : <span style={{ color: '#d46b08' }}>{r.remaining}</span>)}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ borderTop: '1px dashed #f0f0f0', paddingTop: 6, fontSize: 12, color: '#595959' }}>
              {recent.map(d => {
                const cell = r.daily ? r.daily[d] : null;
                return (
                  <div key={d} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontVariantNumeric: 'tabular-nums' }}>
                    <span style={{ color: '#8c8c8c', width: 46 }}>{d.slice(5)}</span>
                    {cell ? (
                      <span>
                        {Number(cell.fans).toLocaleString()}
                        {cell.growth !== null && cell.growth !== undefined && (
                          <span style={{ marginLeft: 6, color: cell.growth > 0 ? '#389e0d' : cell.growth < 0 ? '#cf1322' : '#8c8c8c' }}>
                            ({cell.growth > 0 ? `+${cell.growth}` : cell.growth})
                          </span>
                        )}
                      </span>
                    ) : <span style={{ color: '#d9d9d9' }}>—</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );

  const cookieColumns = [
    { title: '名称', dataIndex: 'name', width: 100, render: v => v || '-' },
    { title: '范围', dataIndex: 'scope', width: 80, render: v => v === 'global' ? <Tag>全局</Tag> : <Tag color="blue">主体</Tag> },
    { title: '主体ID', dataIndex: 'supplier_id', width: 80, render: v => v || '-' },
    { title: 'Cookie', dataIndex: 'cookie_masked', ellipsis: true },
    { title: '失败次数', dataIndex: 'fail_count', width: 90 },
    { title: '最近使用', dataIndex: 'last_used_at', width: 160, render: v => v ? dayjs(v).format('MM-DD HH:mm') : '-' },
    { title: '状态', dataIndex: 'enabled', width: 80, render: v => v ? <Tag color="green">启用</Tag> : <Tag color="red">停用</Tag> },
    {
      title: '操作', key: 'action', width: 140,
      render: (_, rec) => {
        const canManage = isSuperAdmin || (rec.scope === 'supplier' && Number(rec.supplier_id) === Number(user.supplier_id));
        if (!canManage) return '-';
        return (
          <Space size={0}>
            <Button type="link" size="small" onClick={() => toggleCookie(rec)}>{rec.enabled ? '停用' : '启用'}</Button>
            <Button type="link" size="small" danger onClick={() => deleteCookie(rec)}>删除</Button>
          </Space>
        );
      }
    }
  ];

  const filteredCookies = cookies.filter(c => cookieTab === 'global' ? c.scope === 'global' : c.scope === 'supplier');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>涨粉监控</h3>
        <div style={isMobile
          ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, width: '100%' }
          : { display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button block={isMobile} icon={<CopyOutlined />} disabled={selectedRowKeys.length === 0} onClick={handleCopyAccounts}>
            复制抖音号{selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
          </Button>
          <Dropdown
            menu={{
              items: [
                { key: 'filtered', label: '导出筛选数据', onClick: () => handleExport('filtered') },
                { key: 'selected', label: '导出选中数据', disabled: selectedRowKeys.length === 0, onClick: () => handleExport('selected') }
              ]
            }}
          >
            <Button block={isMobile} icon={<DownloadOutlined />}>导出</Button>
          </Dropdown>
          <Button block={isMobile} icon={<KeyOutlined />} onClick={openCookies}>Cookie 管理</Button>
          <Button block={isMobile} icon={<UnorderedListOutlined />} onClick={openTasks}>任务管理</Button>
          <div style={isMobile ? { gridColumn: '1 / -1' } : undefined}>
            <Dropdown
              menu={{
                items: [
                  { key: 'filtered', label: `拉取筛选结果（${summary.total || 0}）`, onClick: () => startFetch('filtered') },
                  { key: 'selected', label: `拉取选中（${selectedRowKeys.length}）`, disabled: selectedRowKeys.length === 0, onClick: () => startFetch('selected') }
                ]
              }}
            >
              <Button block={isMobile} type="primary" icon={<PlayCircleOutlined />} loading={fetching} disabled={!canEdit}>
                拉取最新粉丝
              </Button>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* 搜索 */}
      <div style={isMobile
        ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }
        : { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <Radio.Group
          value={searchMode}
          onChange={e => setSearchMode(e.target.value)}
          buttonStyle="solid"
          size="small"
          style={isMobile ? { gridColumn: '1 / -1', width: '100%', display: 'flex' } : undefined}
        >
          <Radio.Button value="simple" style={isMobile ? { flex: 1, textAlign: 'center' } : undefined}>简单搜索</Radio.Button>
          <Radio.Button value="batch" style={isMobile ? { flex: 1, textAlign: 'center' } : undefined}>批量搜索</Radio.Button>
        </Radio.Group>
        <Select
          popupMatchSelectWidth={false}
          value={filters.search_field}
          onChange={v => setFilters(prev => ({ ...prev, search_field: v }))}
          style={{ width: isMobile ? '100%' : 120 }}
          size={isMobile ? 'small' : 'middle'}
          options={[
            { label: '全部字段', value: 'all' },
            { label: '抖音号', value: 'account_name' },
            { label: '专属链接', value: 'exclusive_link' },
            { label: '备注', value: 'remark' },
            { label: '受理员工', value: 'handler' },
            { label: '小组', value: 'team' },
            { label: '群名', value: 'group_name' },
            { label: '对接人', value: 'contact_person' }
          ]}
        />
        {searchMode === 'batch' && (
          <Select
            popupMatchSelectWidth={false}
            value={filters.match_type}
            onChange={v => setFilters(prev => ({ ...prev, match_type: v }))}
            style={{ width: isMobile ? '100%' : 90 }}
            size={isMobile ? 'small' : 'middle'}
            options={[{ label: '模糊', value: 'fuzzy' }, { label: '精准', value: 'exact' }]}
          />
        )}
        {searchMode === 'simple' ? (
          <Input
            allowClear
            placeholder="搜索"
            style={isMobile ? { width: '100%', gridColumn: '1 / -1' } : { flex: 1, minWidth: 160 }}
            size={isMobile ? 'small' : 'middle'}
            value={filters.keyword}
            onChange={e => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
            onPressEnter={() => { setPage(1); loadData(1, pageSize, filters); }}
          />
        ) : (
          <Input.TextArea
            placeholder="一行一个关键词"
            rows={1}
            style={isMobile ? { width: '100%', gridColumn: '1 / -1' } : { flex: 1, minWidth: 160 }}
            value={filters.keywords}
            onChange={e => setFilters(prev => ({ ...prev, keywords: e.target.value }))}
          />
        )}
        <Button
          size={isMobile ? 'small' : 'middle'}
          type="primary"
          block={isMobile}
          style={isMobile ? { gridColumn: '1 / -1' } : undefined}
          onClick={() => { setPage(1); loadData(1, pageSize, filters); }}
        >搜索</Button>
      </div>

      {/* 筛选条件 */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={labelStyle}>状态</div>
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            maxTagCount="responsive"
            popupMatchSelectWidth={false}
            value={filters.status_ids}
            onChange={v => {
              setPage(1);
              const hasAll = v.includes('__all__');
              const hadAll = (filters.status_ids || []).includes('__all__');
              // 点「全部」时清空其它状态；在「全部」基础上再选具体状态则取消「全部」
              const next = hasAll && !hadAll ? ['__all__'] : v.filter(k => k !== '__all__');
              setFilters(prev => ({ ...prev, status_ids: next }));
            }}
            options={[{ label: '全部', value: '__all__' }, ...statuses.map(st => ({ label: st.name, value: st.id }))]}
            placeholder="全部状态"
          />
        </div>
        <div>
          <div style={labelStyle}>平台</div>
          <Select
            allowClear
            style={{ width: '100%' }}
            value={filters.platform}
            onChange={v => { setPage(1); setFilters(prev => ({ ...prev, platform: v })); }}
            options={platforms.map(pf => ({ label: pf.name, value: pf.name }))}
            placeholder="全部平台"
          />
        </div>
        <div>
          <div style={labelStyle}>主体</div>
          {isAdmin ? (
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount="responsive"
              style={{ width: '100%' }}
              popupMatchSelectWidth={false}
              value={filters.supplier_id}
              onChange={v => { setPage(1); setFilters(prev => ({ ...prev, supplier_id: v })); }}
              options={suppliers.map(sp => ({ label: sp.name, value: sp.id }))}
              placeholder="全部主体"
            />
          ) : (
            <Select
              disabled
              style={{ width: '100%' }}
              value={user.supplier_id || undefined}
              options={user.supplier_id ? [{ label: user.supplier_name || '我的主体', value: user.supplier_id }] : []}
              placeholder="我的主体"
            />
          )}
        </div>
        <div>
          <div style={labelStyle}>涨粉方式</div>
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            value={filters.growth_method}
            onChange={v => { setPage(1); setFilters(prev => ({ ...prev, growth_method: v })); }}
            options={growthMethods.map(gm => ({ label: gm.name, value: gm.name }))}
            placeholder="全部涨粉方式"
          />
        </div>
        <div>
          <div style={labelStyle}>达标状态</div>
          <Select
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            value={filters.target_status || 'all'}
            onChange={v => { setPage(1); setFilters(prev => ({ ...prev, target_status: v })); }}
            options={[
              { label: '全部', value: 'all' },
              { label: '已达标', value: 'met' },
              { label: '未达标', value: 'unmet' }
            ]}
          />
        </div>
        <div>
          <div style={labelStyle}>小组</div>
          <Select
            mode="multiple"
            allowClear
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            value={filters.team}
            onChange={v => { setPage(1); setFilters(prev => ({ ...prev, team: v })); }}
            options={teams.map(t => ({ label: t.name, value: t.name }))}
            placeholder="全部小组"
          />
        </div>
        <div>
          <div style={labelStyle}>受理员工</div>
          <Input
            allowClear
            style={{ width: '100%' }}
            value={filters.handler}
            onChange={e => setFilters(prev => ({ ...prev, handler: e.target.value || undefined }))}
            placeholder="输入员工"
          />
        </div>
        <RangeFilter
          label="最终有效粉"
          presets={RANGE_PRESETS}
          preset={effectiveFansPreset}
          min={filters.effective_fans_min}
          max={filters.effective_fans_max}
          onChange={({ preset, min, max }) => { setEffectiveFansPreset(preset); setPage(1); setFilters(prev => ({ ...prev, effective_fans_min: min, effective_fans_max: max })); }}
        />
        <RangeFilter
          label="初始粉丝"
          presets={RANGE_PRESETS}
          preset={initialFansPreset}
          min={filters.initial_fans_min}
          max={filters.initial_fans_max}
          onChange={({ preset, min, max }) => { setInitialFansPreset(preset); setPage(1); setFilters(prev => ({ ...prev, initial_fans_min: min, initial_fans_max: max })); }}
        />
        <RangeFilter
          label="最终粉丝"
          presets={RANGE_PRESETS}
          preset={finalFansPreset}
          min={filters.final_fans_min}
          max={filters.final_fans_max}
          onChange={({ preset, min, max }) => { setFinalFansPreset(preset); setPage(1); setFilters(prev => ({ ...prev, final_fans_min: min, final_fans_max: max })); }}
        />
        <RangeFilter
          label="涨粉量"
          presets={GROWTH_PRESETS}
          preset={growthCountPreset}
          min={filters.growth_count_min}
          max={filters.growth_count_max}
          onChange={({ preset, min, max }) => { setGrowthCountPreset(preset); setPage(1); setFilters(prev => ({ ...prev, growth_count_min: min, growth_count_max: max })); }}
        />
        <div>
          <div style={labelStyle}>下单时间</div>
          <RangePicker style={{ width: '100%' }} value={filters.dateRange} onChange={v => { setPage(1); setFilters(prev => ({ ...prev, dateRange: v })); }} />
        </div>
        <div>
          <div style={labelStyle}>完成日期</div>
          <RangePicker style={{ width: '100%' }} value={filters.completeDateRange} onChange={v => { setPage(1); setFilters(prev => ({ ...prev, completeDateRange: v })); }} placeholder={['完成日期起', '完成日期止']} />
        </div>
        <div>
          <div style={labelStyle}>最新修改时间</div>
          <RangePicker style={{ width: '100%' }} value={filters.updatedDateRange} onChange={v => { setPage(1); setFilters(prev => ({ ...prev, updatedDateRange: v })); }} placeholder={['修改时间起', '修改时间止']} />
        </div>
        <div>
          <div style={labelStyle}>明细日期范围</div>
          <Select
            style={{ width: '100%' }}
            value={filters.days}
            onChange={v => { setPage(1); setFilters(prev => ({ ...prev, days: v })); }}
            options={[
              { label: '最近 7 天', value: 7 },
              { label: '最近 14 天', value: 14 },
              { label: '最近 30 天', value: 30 }
            ]}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12, padding: '8px 12px', background: '#fafafa', borderRadius: 8, fontSize: 13 }}>
        <span>监控账号：<b>{summary.total || 0}</b></span>
        <span>今日增长合计：<b style={{ color: '#389e0d' }}>+{summary.today_growth_sum || 0}</b></span>
        <span>0增长：<b style={{ color: '#fa8c16' }}>{summary.zero_growth || 0}</b></span>
        <span>掉粉：<b style={{ color: '#cf1322' }}>{summary.falling || 0}</b></span>
        <span>查询失败：<b>{summary.failed || 0}</b></span>
        <Tooltip
          title={`每拉取一次都会记录一条粉丝快照（成功、失败都记）；超过 ${retention.days} 天的记录由系统每天自动清理${retention.last_run && retention.last_run.at
            ? `。上次清理：${new Date(retention.last_run.at).toLocaleString('zh-CN')}，删除 ${retention.last_run.deleted || 0} 条`
            : '。服务启动 3 分钟后会跑第一次清理'}`}
        >
          <span style={{ color: '#999', cursor: 'help', borderBottom: '1px dashed #d9d9d9' }}>
            粉丝记录保留 {retention.days} 天，超期自动清理
          </span>
        </Tooltip>
        <span style={{ marginLeft: 'auto' }}>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => loadData()}>刷新</Button>
        </span>
      </div>

      {isMobile ? mobileCards : (
        <Table
          rowKey="order_id"
          columns={columns}
          dataSource={data}
          loading={loading}
          size="small"
          scroll={{ x: 1800 }}
          onChange={(pagination, filters, sorter, extra) => {
            // 分页变化由 pagination.onChange 处理，这里不要再重置页码
            if (extra && extra.action === 'paginate') return;
            setPage(1);
            if (sorter && sorter.order) {
              setSortField(sorter.columnKey || sorter.field);
              setSortOrder(sorter.order);
            } else {
              setSortField(undefined);
              setSortOrder(undefined);
            }
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            fixed: true
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200', '500'],
            showTotal: t => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); }
          }}
        />
      )}

      {isMobile && (
        <div style={{ marginTop: 12 }}>
          <div style={{ textAlign: 'center', fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>共 {total} 条</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <Select
              size="small"
              value={pageSize}
              style={{ width: 100 }}
              onChange={(v) => { setPage(1); setPageSize(v); }}
              options={[20, 50, 100, 200, 500].map(v => ({ label: `${v} 条/页`, value: v }))}
            />
            <Space size={6}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</Button>
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>{page} / {Math.max(1, Math.ceil(total / pageSize))}</span>
              <Button size="small" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage(page + 1)}>下一页</Button>
            </Space>
          </div>
        </div>
      )}

      {/* 拉取进度 */}
      <Modal
        title="正在拉取粉丝数"
        open={fetching}
        footer={null}
        closable={false}
        maskClosable={false}
        width={420}
      >
        {job && (
          <div>
            <Progress
              percent={job.total ? Math.round((job.done / job.total) * 100) : 0}
              status={job.status === 'running' ? 'active' : (job.status === 'error' ? 'exception' : 'success')}
            />
            <div style={{ marginTop: 8, fontSize: 13, color: '#595959' }}>
              已查询 {job.done} / {job.total}　成功 {job.success}　失败 {job.failed}
            </div>
            {(() => {
              const started = fetchStartRef.current || Date.now();
              const elapsed = Math.max(0, Math.floor((tick - started) / 1000));
              const done = job.done || 0;
              const total = job.total || 0;
              const avg = done > 0 ? elapsed / done : 1.2;
              const remain = job.status === 'running' ? Math.ceil(avg * Math.max(total - done, 0)) : 0;
              return (
                <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
                  已用 {formatDuration(elapsed)}　预计剩余 {job.status === 'running' ? formatDuration(remain) : '0秒'}
                  {job.status === 'running' && total > 0 ? `（总计约 ${formatDuration(Math.ceil(avg * total))}）` : ''}
                </div>
              );
            })()}
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <Button onClick={() => setFetching(false)}>后台继续</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* 任务管理 */}
      <Drawer
        title="任务管理"
        open={taskVisible}
        onClose={() => setTaskVisible(false)}
        width={isMobile ? '100%' : 900}
        extra={<Button size="small" icon={<ReloadOutlined />} onClick={() => fetchTasks()}>刷新</Button>}
      >
        <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
          中断或暂停的任务可从上次处理位置继续；终止后不可恢复。
        </div>
        {isMobile ? (
          <div>
            {tasks.length === 0 && (
              <div style={{ textAlign: 'center', color: '#bfbfbf', padding: '24px 0' }}>暂无任务</div>
            )}
            {tasks.map(r => {
              const m = JOB_STATUS_META[r.status] || { text: r.status, color: 'default' };
              return (
                <div key={r.id} style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{String(r.id).replace(/^fm_/, '').slice(0, 14)}</span>
                    <Tag color={m.color}>{m.text}</Tag>
                  </div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>
                    {r.started_at ? dayjs(r.started_at).format('MM-DD HH:mm:ss') : '—'}
                    <span style={{ marginLeft: 8 }}>
                      成功 <span style={{ color: '#389e0d' }}>{r.success}</span> / 失败 <span style={{ color: '#cf1322' }}>{r.failed}</span>
                    </span>
                  </div>
                  <Progress percent={r.total ? Math.round((r.done / r.total) * 100) : 0} size="small" showInfo={false} />
                  <div style={{ fontSize: 12, color: '#595959', margin: '4px 0 8px' }}>
                    {r.done} / {r.total}{r.message ? ` · ${r.message}` : ''}
                  </div>
                  {renderTaskActions(r)}
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
              <span style={{ fontSize: 12, color: '#8c8c8c' }}>共 {taskTotal} 个任务</span>
              <Space size={6}>
                <Button size="small" disabled={taskPage <= 1} onClick={() => setTaskPage(taskPage - 1)}>上一页</Button>
                <span style={{ fontSize: 12, color: '#8c8c8c' }}>{taskPage} / {Math.max(1, Math.ceil(taskTotal / taskPageSize))}</span>
                <Button size="small" disabled={taskPage >= Math.ceil(taskTotal / taskPageSize)} onClick={() => setTaskPage(taskPage + 1)}>下一页</Button>
              </Space>
            </div>
          </div>
        ) : (
        <Table
          rowKey="id"
          size="small"
          loading={taskLoading}
          dataSource={tasks}
          scroll={{ x: 780 }}
          pagination={{
            current: taskPage,
            pageSize: taskPageSize,
            total: taskTotal,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: t => `共 ${t} 个任务`,
            onChange: (p, ps) => { setTaskPage(p); setTaskPageSize(ps); }
          }}
          columns={[
            {
              title: '任务', dataIndex: 'id', width: 160,
              render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{String(v).replace(/^fm_/, '').slice(0, 14)}</span>
            },
            {
              title: '来源', dataIndex: 'trigger_type', width: 150,
              render: (v, r) => (
                v === 'scheduled'
                  ? <Tooltip title={r.schedule_name || ''}><Tag color="purple">定时 · {r.schedule_name || '已删除任务'}</Tag></Tooltip>
                  : <Tag>手动</Tag>
              )
            },
            {
              title: '开始时间', dataIndex: 'started_at', width: 150,
              render: v => v ? dayjs(v).format('MM-DD HH:mm:ss') : '—'
            },
            {
              title: '状态', dataIndex: 'status', width: 90,
              render: v => {
                const m = JOB_STATUS_META[v] || { text: v, color: 'default' };
                return <Tag color={m.color}>{m.text}</Tag>;
              }
            },
            {
              title: '进度', width: 160,
              render: (_, r) => (
                <div>
                  <div style={{ fontSize: 12 }}>{r.done} / {r.total}</div>
                  <Progress percent={r.total ? Math.round((r.done / r.total) * 100) : 0} size="small" showInfo={false} />
                </div>
              )
            },
            {
              title: '成功 / 失败', width: 100,
              render: (_, r) => (
                <span>
                  <span style={{ color: '#389e0d' }}>{r.success}</span>
                  <span style={{ color: '#bfbfbf' }}> / </span>
                  <span style={{ color: '#cf1322' }}>{r.failed}</span>
                </span>
              )
            },
            {
              title: '说明', dataIndex: 'message', ellipsis: true,
              render: v => v ? <Tooltip title={v}><span style={{ fontSize: 12 }}>{v}</span></Tooltip> : '—'
            },
            {
              title: '操作', width: 180, fixed: 'right',
              render: (_, r) => renderTaskActions(r)
            }
          ]}
        />
        )}
      </Drawer>

      {/* Cookie 管理 */}
      <Modal title="Cookie 管理" open={cookieVisible} onCancel={() => setCookieVisible(false)} footer={null} width={isMobile ? '96%' : 720}>
        <Tabs
          activeKey={cookieTab}
          onChange={setCookieTab}
          items={[
            ...(isSuperAdmin ? [{ key: 'global', label: '全局' }] : []),
            { key: 'supplier', label: isSuperAdmin ? '主体' : '我的主体' }
          ]}
        />
        <Form form={cookieForm} layout="vertical" style={{ marginBottom: 12 }}>
          <Space align="start" wrap>
            <Form.Item name="name" label="名称（备注）" style={{ marginBottom: 8 }}>
              <Input placeholder="如：公用1" style={{ width: 160 }} />
            </Form.Item>
            {cookieTab === 'supplier' && (
              <Form.Item name="supplier_id" label="主体" rules={[{ required: true, message: '请选择主体' }]} style={{ marginBottom: 8 }}>
                <Select
                  style={{ width: 180 }}
                  showSearch
                  optionFilterProp="label"
                  options={suppliers.map(s => ({ label: s.name, value: s.id }))}
                  placeholder="选择主体"
                  disabled={!isSuperAdmin}
                />
              </Form.Item>
            )}
          </Space>
          <Form.Item name="cookie" label="Cookie 内容" rules={[{ required: true, message: '请粘贴 Cookie' }]} style={{ marginBottom: 8 }}>
            <TextArea rows={4} placeholder="粘贴完整 Cookie" />
          </Form.Item>
          <Button type="primary" onClick={() => saveCookie(cookieTab)}>保存</Button>
        </Form>
        <Table
          rowKey="id"
          size="small"
          columns={cookieColumns}
          dataSource={filteredCookies}
          pagination={false}
          scroll={{ x: 700 }}
        />
      </Modal>
    </div>
  );
}

const labelStyle = { fontSize: 12, color: '#8c8c8c', marginBottom: 4 };

export default FanMonitor;
